// components/UserProfile/UserDropdown.js
import React, { useState } from 'react';
import { Typography, Menu, MenuItem, Avatar, Divider, ListItemIcon, Dialog, DialogContent } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import EditIcon from '@mui/icons-material/Edit';
import HelpIcon from '@mui/icons-material/Help';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CakeIcon from '@mui/icons-material/Cake';
import GroupsIcon from '@mui/icons-material/Groups';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import styles from './UserProfile.module.css';
import LogoutButton from '../../components/Login/LogoutButton';
import { getInitialsFromEmail } from '../../utils/userUtils';
import { formatBirthdayDate } from '../../utils/dateUtils';
import BirthdayCalendar from '../Birthday/BirthdayCalendar';
import Leaderboard from '../Leaderboard/leaderboard';

/**
 * Simplified UserDropdown component with direct actions
 */
const UserDropdown = ({ 
  anchorEl, 
  open, 
  onClose, 
  userProfile, 
  userEmail, 
  userName, 
  userInitial, 
  profilePicture, 
  userGroups, 
  onEditProfile,
  onProfilePictureClick
}) => {
  const [modalType, setModalType] = useState(null);

  const handleMenuClose = () => {
    onClose();
  };

  const handleProfilePictureClick = () => {
    onProfilePictureClick();
    handleMenuClose();
  };

  const handleEditProfile = () => {
    onEditProfile();
    handleMenuClose();
  };

  const handleHelpSupport = () => {
    window.open('https://meltwater.slack.com/archives/C066HEYFYH5', '_blank');
    handleMenuClose();
  };

  return (
    <>
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={handleMenuClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      disableScrollLock={true}
      disablePortal={true}
      BackdropProps={{
        invisible: true,
      }}
      MenuListProps={{
        disablePadding: true,
      }}
      PaperProps={{
        elevation: 0,
        sx: {
          overflow: 'visible',
          filter: 'drop-shadow(0px 4px 20px rgba(0,0,0,0.08))',
          mt: 1.5,
          borderRadius: '12px',
          border: '1px solid rgba(0,0,0,0.08)',
          minWidth: '320px',
          maxWidth: '360px',
          '&:before': {
            content: '""',
            display: 'block',
            position: 'absolute',
            top: 0,
            right: 16,
            width: 10,
            height: 10,
            bgcolor: 'background.paper',
            transform: 'translateY(-50%) rotate(45deg)',
            zIndex: 0,
            border: '1px solid rgba(0,0,0,0.08)',
            borderBottom: 'none',
            borderRight: 'none',
          },
        },
      }}
    >
      {/* Header with Profile Info */}
      <div className={styles.menuHeader}>
        <div className={styles.profileSection}>
          <div 
            className={styles.profileAvatar} 
            onClick={handleProfilePictureClick}
            style={{ cursor: 'pointer' }}
          >
            {profilePicture ? (
              <Avatar 
                className={styles.avatarLarge}
                src={profilePicture}
                alt={userName}
              />
            ) : (
              <Avatar className={styles.avatarLarge}>
                {userInitial || (userEmail ? getInitialsFromEmail(userEmail) : 'U')}
              </Avatar>
            )}
            <div className={styles.cameraOverlay}>
              <PhotoCameraIcon fontSize="small" />
            </div>
          </div>
          <div className={styles.profileInfo}>
            <Typography variant="h6" className={styles.profileName}>
              {userProfile?.preferredName || userName}
            </Typography>
            <Typography variant="body2" className={styles.profileEmail}>
              {userEmail}
            </Typography>
            {userProfile?.statusMessage && (
              <Typography variant="caption" className={styles.statusMessage}>
                {userProfile.statusMessage}
              </Typography>
            )}
          </div>
        </div>
      </div>
      
      {/* User Information Section */}
      <div className={styles.menuOptions}>
        <MenuItem className={`${styles.menuItem} ${styles.infoMenuItem}`} disableRipple>
          <ListItemIcon>
            <GroupsIcon fontSize="small" />
          </ListItemIcon>
          <div className={styles.menuItemContent}>
            <Typography variant="body2" className={styles.menuItemTitle}>
              Groups
            </Typography>
            <Typography variant="caption" className={`${styles.menuItemSubtitle} ${!userGroups?.length ? styles.notSet : ''}`}>
              {userGroups?.length > 0 ? 
                `${userGroups[0].name || userGroups[0]}${userGroups.length > 1 ? ` +${userGroups.length - 1}` : ''}` : 
                "Not set"}
            </Typography>
          </div>
        </MenuItem>
      
        <MenuItem className={`${styles.menuItem} ${styles.infoMenuItem}`} disableRipple>
          <ListItemIcon>
            <LocationOnIcon fontSize="small" />
          </ListItemIcon>
          <div className={styles.menuItemContent}>
            <Typography variant="body2" className={styles.menuItemTitle}>
              Location
            </Typography>
            <Typography variant="caption" className={`${styles.menuItemSubtitle} ${!userProfile?.city ? styles.notSet : ''}`}>
              {userProfile?.city ? userProfile.city : "Not set"}
            </Typography>
          </div>
        </MenuItem>
        
        <MenuItem className={`${styles.menuItem} ${styles.infoMenuItem}`} disableRipple>
          <ListItemIcon>
            <AccessTimeIcon fontSize="small" />
          </ListItemIcon>
          <div className={styles.menuItemContent}>
            <Typography variant="body2" className={styles.menuItemTitle}>
              Timezone
            </Typography>
            <Typography variant="caption" className={`${styles.menuItemSubtitle} ${!userProfile?.timezone ? styles.notSet : ''}`}>
              {userProfile?.timezone ? userProfile.timezone : "Not set"}
            </Typography>
          </div>
        </MenuItem>
        
        <MenuItem className={`${styles.menuItem} ${styles.infoMenuItem}`} disableRipple>
          <ListItemIcon>
            <CakeIcon fontSize="small" />
          </ListItemIcon>
          <div className={styles.menuItemContent}>
            <Typography variant="body2" className={styles.menuItemTitle}>
              Birthday
            </Typography>
            <Typography variant="caption" className={`${styles.menuItemSubtitle} ${!userProfile?.birthday ? styles.notSet : ''}`}>
              {userProfile?.birthday ? 
                formatBirthdayDate(userProfile.birthday) : 
                "Not set"}
            </Typography>
          </div>
        </MenuItem>
      </div>

      <Divider className={styles.divider} />

      {/* Features Section */}
      <div className={styles.menuOptions}>
        <MenuItem 
          className={styles.menuItem}
          onClick={() => {
            setModalType('birthday');
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <CakeIcon fontSize="small" />
          </ListItemIcon>
          <div className={styles.menuItemContent}>
            <Typography variant="body2" className={styles.menuItemTitle}>
              Birthday Calendar
            </Typography>
            <Typography variant="caption" className={styles.menuItemSubtitle}>
              View and manage birthday notifications
            </Typography>
          </div>
        </MenuItem>

        <MenuItem 
          className={styles.menuItem}
          onClick={() => {
            setModalType('leaderboard');
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <LeaderboardIcon fontSize="small" />
          </ListItemIcon>
          <div className={styles.menuItemContent}>
            <Typography variant="body2" className={styles.menuItemTitle}>
              Leaderboard
            </Typography>
            <Typography variant="caption" className={styles.menuItemSubtitle}>
              View achievements and rankings
            </Typography>
          </div>
        </MenuItem>
      </div>

      <Divider className={styles.divider} />

      {/* Direct Menu Options */}
      <div className={styles.menuOptions}>
        <MenuItem 
          className={styles.menuItem}
          onClick={handleEditProfile}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <div className={styles.menuItemContent}>
            <Typography variant="body2" className={styles.menuItemTitle}>
              Manage your profile
            </Typography>
            <Typography variant="caption" className={styles.menuItemSubtitle}>
              Edit personal info and preferences
            </Typography>
          </div>
        </MenuItem>

        <MenuItem 
          className={styles.menuItem}
          onClick={handleHelpSupport}
        >
          <ListItemIcon>
            <HelpIcon fontSize="small" />
          </ListItemIcon>
          <div className={styles.menuItemContent}>
            <Typography variant="body2" className={styles.menuItemTitle}>
              Help & Support
            </Typography>
            <Typography variant="caption" className={styles.menuItemSubtitle}>
              For help on issues and suggestions
            </Typography>
          </div>
        </MenuItem>
      </div>

      <Divider className={styles.divider} />

      {/* Logout */}
      <div className={styles.logoutSection}>
        <MenuItem className={styles.logoutMenuItem} onClick={handleMenuClose}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" className={styles.logoutIcon} />
          </ListItemIcon>
          <LogoutButton onClick={handleMenuClose} className={styles.logoutButton}>
            Sign out
          </LogoutButton>
        </MenuItem>
      </div>
    </Menu>

    {/* Birthday Calendar */}
    <BirthdayCalendar 
      open={modalType === 'birthday'} 
      onClose={() => setModalType(null)} 
    />

    {/* Modal for Leaderboard */}
    <Dialog
      open={modalType === 'leaderboard'}
      onClose={() => setModalType(null)}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          borderRadius: '12px'
        }
      }}
    >
      <DialogContent>
        <Leaderboard />
      </DialogContent>
    </Dialog>
    </>
  );
};

export default UserDropdown;
